import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  STOREFRONT_MENU_ICONS,
  type StorefrontMenuIcon,
  type StorefrontSummary,
} from '@aeris/contracts'
import {
  Badge,
  Button,
  InlineAlert,
  QueryState,
  Section,
  Select,
  Switch,
  notify,
} from '@aeris/ui'

import {
  ApiError,
  fetchStorefrontMenu,
  saveStorefrontMenu,
  type StorefrontMenuCategory,
} from '../lib/api.ts'
import { ICON_LABELS, withIpatingaMenu } from '../lib/storefront-menu.ts'

/**
 * O menu de categorias da loja online (F270).
 *
 * O menu "Categorias" do site mostrava toda categoria com produto — inclusive
 * as de peça (tela, bateria, carcaça…), que são da Pesquisa por Peça. Aqui a
 * loja diz o que aparece, em que ordem e com que ícone. Tirar do menu não
 * esconde o produto: a Pesquisa por Peça continua achando.
 */

/** O que a tela manda: a posição é a ordem da lista, de cima e de cada pai. */
const itemsOf = (categories: readonly StorefrontMenuCategory[]) =>
  categories.flatMap((category, index) => [
    {
      categoryId: category.id,
      inMenu: category.inMenu,
      inTabs: category.inTabs,
      position: index,
      icon: category.icon as StorefrontMenuIcon | null,
    },
    ...category.children.map((child, childIndex) => ({
      categoryId: child.id,
      inMenu: child.inMenu,
      inTabs: child.inTabs,
      position: childIndex,
      icon: null,
    })),
  ])

const swapped = <T,>(list: readonly T[], index: number, delta: number): T[] => {
  const copy = [...list]
  const a = copy[index]
  const b = copy[index + delta]
  if (a === undefined || b === undefined) return copy
  copy[index] = b
  copy[index + delta] = a
  return copy
}

export const StorefrontMenu = ({
  store,
}: {
  store: StorefrontSummary
}): ReactNode => {
  const queryClient = useQueryClient()
  const key = ['storefront-menu', store.id]
  const menu = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) => await fetchStorefrontMenu(store.id, signal),
  })
  /** O rascunho: nulo enquanto ninguém mexeu. */
  const [draft, setDraft] = useState<StorefrontMenuCategory[] | null>(null)
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())

  const rows = draft ?? menu.data ?? []

  const save = useMutation({
    mutationFn: async () =>
      await saveStorefrontMenu(store.id, { items: itemsOf(rows) }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(key, saved)
      setDraft(null)
      notify.success('Menu da loja salvo. O site mostra em até um minuto.')
      await queryClient.invalidateQueries({ queryKey: key })
    },
    onError: (cause) => {
      notify.error(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar o menu.'
      )
    },
  })

  const change = (next: StorefrontMenuCategory[]): void => {
    setDraft(next)
  }
  const patch = (id: string, values: Partial<StorefrontMenuCategory>): void => {
    change(
      rows.map((category) =>
        category.id === id ? { ...category, ...values } : category
      )
    )
  }
  const patchChild = (
    parentId: string,
    childId: string,
    values: { inMenu?: boolean; inTabs?: boolean }
  ): void => {
    change(
      rows.map((category) =>
        category.id === parentId
          ? {
              ...category,
              children: category.children.map((child) =>
                child.id === childId ? { ...child, ...values } : child
              ),
            }
          : category
      )
    )
  }

  const shown = rows.filter((category) => category.inMenu).length

  return (
    <Section
      title="Menu da loja"
      description="As categorias do menu “Categorias” do site, nesta ordem. As de peça ficam de fora: elas são da Pesquisa por Peça."
      action={
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={rows.length === 0}
            onClick={() => {
              change(withIpatingaMenu(rows))
            }}
          >
            Usar o menu de Ipatinga
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={draft === null}
            loading={save.isPending}
            onClick={() => {
              save.mutate()
            }}
          >
            Salvar menu
          </Button>
        </div>
      }
    >
      <QueryState
        isPending={menu.isPending}
        isError={menu.isError}
        error={menu.error}
        onRetry={() => void menu.refetch()}
        what="o menu da loja"
        isEmpty={rows.length === 0}
        empty={
          <InlineAlert tone="info">
            Nenhuma categoria ainda. Em Produtos, “Importar planilha de
            categorias” cria a árvore.
          </InlineAlert>
        }
      >
        <p className="text-(length:--text-sm) text-(--text-secondary)">
          {shown} de {rows.length} no menu
          {draft === null ? '' : ' · alterações não salvas'}
        </p>
        <ul className="flex flex-col">
          {rows.map((category, index) => {
            const expanded = open.has(category.id)
            return (
              <li
                key={category.id}
                className="flex flex-col border-b border-(--border-subtle) py-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={
                      expanded
                        ? `Fechar subcategorias de ${category.name}`
                        : `Abrir subcategorias de ${category.name}`
                    }
                    disabled={category.children.length === 0}
                    onClick={() => {
                      const next = new Set(open)
                      if (expanded) next.delete(category.id)
                      else next.add(category.id)
                      setOpen(next)
                    }}
                  >
                    {expanded ? (
                      <ChevronDown aria-hidden="true" className="h-4 w-4" />
                    ) : (
                      <ChevronRight aria-hidden="true" className="h-4 w-4" />
                    )}
                  </Button>
                  <Switch
                    aria-label={`${category.name} no menu`}
                    checked={category.inMenu}
                    onCheckedChange={(checked) => {
                      patch(category.id, { inMenu: checked })
                    }}
                  />
                  <span
                    className={
                      category.inMenu
                        ? 'min-w-40 flex-1 font-(--weight-medium)'
                        : 'min-w-40 flex-1 text-(--text-secondary)'
                    }
                  >
                    {category.name}
                  </span>
                  {/*
                    F292: fora do menu, a categoria vira aba na Pesquisa por
                    Peça do site. Esta chave tira a aba ("tela" repetia Telas).
                  */}
                  {category.inMenu ? null : (
                    <label className="flex items-center gap-1 text-(length:--text-xs) text-(--text-secondary)">
                      <Switch
                        aria-label={`${category.name} como aba na Pesquisa por Peça`}
                        checked={category.inTabs}
                        onCheckedChange={(checked) => {
                          patch(category.id, { inTabs: checked })
                        }}
                      />
                      aba na Pesquisa por Peça
                    </label>
                  )}
                  <Badge tone="neutral">
                    {category.products}{' '}
                    {category.products === 1 ? 'produto' : 'produtos'}
                  </Badge>
                  <Select
                    aria-label={`Ícone de ${category.name}`}
                    className="w-44"
                    value={category.icon ?? ''}
                    disabled={!category.inMenu}
                    onChange={(event) => {
                      patch(category.id, {
                        icon:
                          event.target.value === '' ? null : event.target.value,
                      })
                    }}
                  >
                    <option value="">Ícone automático</option>
                    {STOREFRONT_MENU_ICONS.map((icon) => (
                      <option key={icon} value={icon}>
                        {ICON_LABELS[icon]}
                      </option>
                    ))}
                  </Select>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Subir ${category.name}`}
                    disabled={index === 0}
                    onClick={() => {
                      change(swapped(rows, index, -1))
                    }}
                  >
                    <ArrowUp aria-hidden="true" className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Descer ${category.name}`}
                    disabled={index === rows.length - 1}
                    onClick={() => {
                      change(swapped(rows, index, 1))
                    }}
                  >
                    <ArrowDown aria-hidden="true" className="h-4 w-4" />
                  </Button>
                </div>
                {expanded ? (
                  <ul className="mt-1 ml-10 flex flex-col gap-1">
                    {category.children.map((child) => (
                      <li key={child.id} className="flex items-center gap-2">
                        <Switch
                          aria-label={`${child.name} no menu`}
                          checked={child.inMenu}
                          onCheckedChange={(checked) => {
                            patchChild(category.id, child.id, {
                              inMenu: checked,
                            })
                          }}
                        />
                        <span className="text-(length:--text-sm)">
                          {child.name}
                        </span>
                        <span className="text-(length:--text-xs) text-(--text-muted)">
                          {child.products}
                        </span>
                        {child.inMenu ? null : (
                          <label className="ml-2 flex items-center gap-1 text-(length:--text-xs) text-(--text-secondary)">
                            <Switch
                              aria-label={`${child.name} como aba na Pesquisa por Peça`}
                              checked={child.inTabs}
                              onCheckedChange={(checked) => {
                                patchChild(category.id, child.id, {
                                  inTabs: checked,
                                })
                              }}
                            />
                            aba na Pesquisa por Peça
                          </label>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            )
          })}
        </ul>
      </QueryState>
    </Section>
  )
}
